import React from 'react';

const RateLimiting = () => {
  return (
    <div className="page">
      <div className="page-header">
        <h1>⚡ Rate Limiting</h1>
        <p>
          WhakUp implémente un système de rate limiting pour protéger l'API et garantir une utilisation équitable.
          Comprenez les limites et comment les gérer.
        </p>
      </div>

      <div className="section">
        <h2>🎯 Qu'est-ce que le Rate Limiting ?</h2>
        <p>
          Le rate limiting limite le nombre de requêtes qu'un utilisateur peut faire dans une période donnée.
          Cela protège l'API contre les abus et garantit des performances optimales pour tous les utilisateurs.
        </p>
      </div>

      <div className="section">
        <h2>📊 Limites par Type d'Endpoint</h2>
        <table className="params-table">
          <thead>
            <tr>
              <th>Type d'Endpoint</th>
              <th>Limite</th>
              <th>Période</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>API Générale</strong></td>
              <td>5000 requêtes</td>
              <td>1 heure</td>
              <td>Lecture / navigation (ex: <code>GET /api/v1/campaigns</code>, <code>GET /api/v1/templates</code>)</td>
            </tr>
            <tr>
              <td><strong>Endpoints Sensibles</strong></td>
              <td>1000 requêtes</td>
              <td>1 heure</td>
              <td>Création, modification, suppression (contacts, groupes, envoi de messages)</td>
            </tr>
            <tr>
              <td><strong>Par Workspace</strong></td>
              <td>20 000 requêtes</td>
              <td>1 heure</td>
              <td>Actions volumineuses et listings à l'échelle du workspace</td>
            </tr>
            <tr>
              <td><strong>Authentification</strong></td>
              <td>30 requêtes</td>
              <td>1 minute</td>
              <td>Endpoints d'authentification (<code>/auth</code>, <code>/api/public/auth</code>)</td>
            </tr>
            <tr>
              <td><strong>Webhooks (inbound)</strong></td>
              <td>300 requêtes</td>
              <td>1 minute</td>
              <td>Endpoints de réception de webhooks (<code>/webhook/*</code>)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="section">
        <h2>📡 Headers de Rate Limiting</h2>
        <p>
          Chaque réponse API inclut automatiquement des headers qui indiquent votre statut de rate limiting :
        </p>
        <table className="params-table">
          <thead>
            <tr>
              <th>Header</th>
              <th>Description</th>
              <th>Exemple</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>X-Rate-Limit-Limit</code></td>
              <td>Limite totale de requêtes</td>
              <td>1000</td>
            </tr>
            <tr>
              <td><code>X-Rate-Limit-Remaining</code></td>
              <td>Nombre de requêtes restantes</td>
              <td>999</td>
            </tr>
            <tr>
              <td><code>X-Rate-Limit-Reset</code></td>
              <td>Timestamp de réinitialisation (Unix)</td>
              <td>1640995200</td>
            </tr>
          </tbody>
        </table>

        <div className="code-block">
{`// Exemple de réponse avec headers
HTTP/1.1 200 OK
X-Rate-Limit-Limit: 5000
X-Rate-Limit-Remaining: 4999
X-Rate-Limit-Reset: 1640995200

{
  "data": "..."
}`}
        </div>
      </div>

      <div className="section">
        <h2>❌ Gestion des Limites Dépassées</h2>
        <p>
          Si vous dépassez la limite, vous recevrez une erreur <code>429 Too Many Requests</code> :
        </p>

        <div className="endpoint-card">
          <h4>Réponse d'erreur 429:</h4>
          <div className="code-block">
{`HTTP/1.1 429 Too Many Requests
Retry-After: 3600
Content-Type: application/json

{
  "error": {
    "code": 429,
    "message": "Rate limit exceeded. Try again in 3600 seconds.",
    "retry_after": 3600
  }
}`}
          </div>
        </div>

        <div className="alert alert-warning">
          <strong>⚠️ Important :</strong> Le header <code>Retry-After</code> indique le nombre de secondes
          à attendre avant de pouvoir refaire une requête.
        </div>
      </div>

      <div className="section">
        <h2>💻 Gestion dans votre Code</h2>
        
        <h3>JavaScript / Node.js</h3>
        <div className="code-block">
{`async function makeRequestWithRetry(apiCall) {
  try {
    return await apiCall();
  } catch (error) {
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'];
      console.log(\`Rate limit atteint. Retry dans \${retryAfter} secondes\`);
      
      // Attendre le temps indiqué
      await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
      
      // Réessayer
      return makeRequestWithRetry(apiCall);
    }
    throw error;
  }
}

// Avec vérification des headers
async function makeRequest(url, options) {
  const response = await fetch(url, options);
  
  const limit = response.headers.get('X-Rate-Limit-Limit');
  const remaining = response.headers.get('X-Rate-Limit-Remaining');
  
  if (remaining < 10) {
    console.warn(\`⚠️ Attention: Il ne reste que \${remaining} requêtes sur \${limit}\`);
  }
  
  if (response.status === 429) {
    const retryAfter = response.headers.get('Retry-After');
    throw new Error(\`Rate limit dépassé. Retry dans \${retryAfter}s\`);
  }
  
  return response.json();
}`}
        </div>

        <h3>PHP</h3>
        <div className="code-block">
{`function makeRequestWithRetry($url, $options, $maxRetries = 3) {
    for ($i = 0; $i < $maxRetries; $i++) {
        $response = curl_exec($ch);
        $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
        
        if ($httpCode === 429) {
            $retryAfter = curl_getinfo($ch, CURLINFO_RETRY_AFTER);
            if ($retryAfter > 0) {
                sleep($retryAfter);
                continue;
            }
        }
        
        return $response;
    }
    
    throw new Exception('Rate limit dépassé après plusieurs tentatives');
}`}
        </div>

        <h3>Python</h3>
        <div className="code-block">
{`import time
import requests

def make_request_with_retry(url, headers, max_retries=3):
    for attempt in range(max_retries):
        response = requests.get(url, headers=headers)
        
        if response.status_code == 429:
            retry_after = int(response.headers.get('Retry-After', 60))
            print(f'Rate limit dépassé. Retry dans {retry_after}s')
            time.sleep(retry_after)
            continue
        
        # Vérifier les headers de rate limiting
        limit = response.headers.get('X-Rate-Limit-Limit')
        remaining = response.headers.get('X-Rate-Limit-Remaining')
        
        if int(remaining) < 10:
            print(f'⚠️ Attention: {remaining}/{limit} requêtes restantes')
        
        return response
    
    raise Exception('Rate limit dépassé après plusieurs tentatives')`}
        </div>
      </div>

      <div className="section">
        <h2>🔄 Stratégies de Retry</h2>
        <p>Voici différentes stratégies pour gérer les rate limits :</p>
        
        <h3>1. Retry Simple</h3>
        <p>Attendre le temps indiqué dans <code>Retry-After</code> et réessayer.</p>

        <h3>2. Backoff Exponentiel</h3>
        <div className="code-block">
{`function exponentialBackoff(attempt, baseDelay = 1000) {
    return baseDelay * Math.pow(2, attempt);
}

// Attendre 1s, puis 2s, puis 4s, puis 8s...`}
        </div>

        <h3>3. Queue de Requêtes</h3>
        <p>
          Mettre les requêtes en file d'attente et les exécuter progressivement
          pour ne jamais dépasser les limites.
        </p>
      </div>

      <div className="section">
        <h2>✅ Bonnes Pratiques</h2>
        <ul>
          <li><strong>Surveillez les headers</strong> - Vérifiez toujours <code>X-Rate-Limit-Remaining</code></li>
          <li><strong>Implémentez un retry intelligent</strong> - Ne spammez pas l'API</li>
          <li><strong>Cachez les données</strong> - Évitez les requêtes inutiles</li>
          <li><strong>Batch les requêtes</strong> - Groupez les opérations quand possible</li>
          <li><strong>Gérez les erreurs 429</strong> - Implémentez une logique de retry</li>
        </ul>
      </div>

      <div className="section">
        <h2>📊 Monitoring</h2>
        <p>
          Gardez un œil sur votre consommation de rate limiting en vérifiant régulièrement
          les headers de réponse. Si vous approchez des limites, réduisez la fréquence
          de vos requêtes ou contactez le support pour discuter d'une augmentation de quota.
        </p>
      </div>
    </div>
  );
};

export default RateLimiting;
